import {
  createCertificateIndustryRelationApi,
  updateCertificateIndustryRelationApi,
  useGetCompanySpecificIndustryList,
} from "@/api/services/global/master";
import { TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto } from "@/api/services/global/master/dtos/specificSkillCertificate/get-master-specific-skill-certificate-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { certificateIndustryRelationSchema } from "@/features/master-data/schema/masterDataSpecificSkillCertificateSchema";
import { TCertificateIndustryRelationForm } from "@/features/master-data/types/masterDataSpecificSkillCertificateForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TCertificateIndustryRelationFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedCertificateIndustryRelation?: TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto;
  existingSpecificSkillCertificate: string;
  existingSpecificSkillCertificateId: number;
  setSelectedCertificateIndustryRelationRows: React.Dispatch<React.SetStateAction<TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto[]>>;
};

export const CertificateIndustryRelationFormModal: FC<TCertificateIndustryRelationFormModalProps> = ({
  onClose,
  isOpen,
  selectedCertificateIndustryRelation,
  existingSpecificSkillCertificate,
  existingSpecificSkillCertificateId,
  setSelectedCertificateIndustryRelationRows,
}) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      companySpecificIndustryId: selectedCertificateIndustryRelation ? selectedCertificateIndustryRelation.companySpecificIndustryId : undefined,
    }),
    [selectedCertificateIndustryRelation],
  );

  const { isLoading: isLoadingCompanySpecificIndustryList, companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();

  const methods = useForm<TCertificateIndustryRelationForm>({
    resolver: yupResolver(certificateIndustryRelationSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control } = methods;

  const mapToOutputDto = (
    data: import("@/api/entities/master-specific-skill-certificate-company-specific-industry.entity").TMasterSpecificSkillCertificateCompanySpecificIndustry,
  ): TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto => ({
    id: data.id,
    specificSkillCertificateId: data.specificSkillCertificateId,
    companySpecificIndustryId: data.companySpecificIndustryId,
    order: data.order,
    isHidden: data.isHidden,
    createdAt: data.createdAt ?? "",
    createdBy: data.createdBy ?? 0,
    updatedAt: data.updatedAt,
    updatedBy: data.updatedBy,
    companySpecificIndustry: data.companySpecificIndustry
      ? { id: data.companySpecificIndustry.id, companySpecificIndustry: data.companySpecificIndustry.companySpecificIndustry }
      : undefined,
  });

  const onSubmit = async (data: TCertificateIndustryRelationForm) => {
    if (!existingSpecificSkillCertificateId || !existingSpecificSkillCertificate) return;
    try {
      if (selectedCertificateIndustryRelation) {
        const rs = await updateCertificateIndustryRelationApi(existingSpecificSkillCertificateId, selectedCertificateIndustryRelation.id, {
          companySpecificIndustryId: toNumber(data.companySpecificIndustryId),
          specificSkillCertificateId: toNumber(existingSpecificSkillCertificateId),
        });
        const updated = mapToOutputDto(rs.data);
        setSelectedCertificateIndustryRelationRows((prev) => {
          const arr = prev.map((item) => (toNumber(item.id) === toNumber(updated.id) ? updated : item));
          return arr;
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createCertificateIndustryRelationApi(existingSpecificSkillCertificateId, {
          companySpecificIndustryId: data.companySpecificIndustryId,
          specificSkillCertificateId: toNumber(existingSpecificSkillCertificateId),
        });
        const created = mapToOutputDto(rs.data);
        setSelectedCertificateIndustryRelationRows((prev) => [...prev, created]);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      onClose();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_SPECIFIC_SKILL_CERTIFICATE] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      title={selectedCertificateIndustryRelation ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={onClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={certificateIndustryRelationSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={12}>
            <FormField control={control} name="companySpecificIndustryId" label="分野">
              <Select options={companySpecificIndustryOptions} placeholder="選択してください" loading={isLoadingCompanySpecificIndustryList} />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
